function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.appliedFilters = {};
    this.form = this.querySelector('#FacetFiltersForm');
    this.activeFacetsContainer = this.querySelector('.active-facets-desktop');
    this.onCheckboxClick = this.onCheckboxClick.bind(this);
    this.onFacetRemoveClick = this.onFacetRemoveClick.bind(this);
    this.onFormChangeDebounced = debounce(this.onFormChangeAJAX.bind(this), 500);

    // Attach listeners for custom checkboxes
    this.form.querySelectorAll('.custom-checkbox-wrapper').forEach(wrapper => {
      wrapper.addEventListener('click', this.onCheckboxClick);
    });

    // Attach listeners for active facet removal
    this.activeFacetsContainer.querySelectorAll('facet-remove a').forEach(link => {
      link.addEventListener('click', this.onFacetRemoveClick);
    });

    // Listen for clear all filters
    const clearBtn = this.querySelector('[data-clear-facets-button]');
    if (clearBtn) {
      clearBtn.addEventListener('click', this.onClearFilter.bind(this));
    }

    // Listen for sort change
    const sortSelect = document.querySelector('#SortBy');
    if (sortSelect) {
      sortSelect.addEventListener('change', () => {
        // Add sort param and update filters
        const params = new URLSearchParams(new FormData(this.form));
        params.set('sort_by', sortSelect.value);
        FacetFiltersForm.updateURL(params.toString());
        this.fetchAndUpdate(`${window.location.pathname}?${params.toString()}`);
      });
    }

    // Debounced AJAX for any input change
    this.form.addEventListener('input', this.onFormChangeDebounced);
  }

  onCheckboxClick(e) {
    // Prevent toggling if clicking directly on the input
    if (e.target.tagName === 'INPUT') return;

    const wrapper = e.currentTarget;
    const customCheckbox = wrapper.querySelector('[data-custom-checkbox]');
    customCheckbox.classList.toggle('selected');

    const checkbox = wrapper.querySelector('input[type="checkbox"]');
    checkbox.checked = !checkbox.checked;

    // Trigger debounced AJAX filter update
    this.onFormChangeDebounced();
  }

  onFacetRemoveClick(e) {
    e.preventDefault();
    // AJAX remove facet by fetching the remove URL and updating the grid
    this.fetchAndUpdate(e.currentTarget.getAttribute('href'));
  }

  onClearFilter(e) {
    e.preventDefault();
    const href = e.currentTarget.getAttribute('href');
    FacetFiltersForm.updateURL(href);
    this.fetchAndUpdate(href);
  }

  onFormChangeAJAX() {
    const params = new URLSearchParams(new FormData(this.form)).toString();
    FacetFiltersForm.updateURL(params);
    this.fetchAndUpdate(`${window.location.pathname}${params ? '?' + params : ''}`);
  }

  fetchAndUpdate(url) {
    // Add loading state before fetch
    const oldGrid = document.querySelector('.results-wrapper');
    if (oldGrid) oldGrid.classList.add('is-loading');

    // Track filters from URL
    this.appliedFilters = FacetFiltersForm.parseFiltersFromURL(url);

    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Replace product grid
        const newGrid = doc.querySelector('.results-wrapper');
        if (newGrid && oldGrid) oldGrid.innerHTML = newGrid.innerHTML;

        // Replace active facets
        const newActiveFacets = doc.querySelector('.active-facets-desktop');
        if (newActiveFacets && this.activeFacetsContainer) {
          this.activeFacetsContainer.innerHTML = newActiveFacets.innerHTML;
          this.activeFacetsContainer.querySelectorAll('facet-remove a').forEach(link => {
            link.addEventListener('click', this.onFacetRemoveClick);
          });
        }

        // Update all [data-items-count]
        const newCountEls = doc.querySelectorAll('[data-items-count]');
        const oldCountEls = document.querySelectorAll('[data-items-count]');
        newCountEls.forEach((newEl, idx) => {
          if (oldCountEls[idx]) {
            oldCountEls[idx].textContent = newEl.textContent;
          }
        });

        // Sync checkboxes with applied filters
        this.form.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
          const name = checkbox.name;
          const value = checkbox.value;
          if (this.appliedFilters[name] && this.appliedFilters[name].includes(value)) {
            checkbox.checked = true;
          }
          else {
            checkbox.checked = false;
          }
          checkbox.dispatchEvent(new Event('change'));
        });

        // Re-attach clear button listener
        const clearBtn = this.querySelector('[data-clear-facets-button]');
        if (clearBtn) {
          clearBtn.addEventListener('click', this.onClearFilter.bind(this));
        }

        // Remove loading state after fetch is complete
        if (oldGrid) oldGrid.classList.remove('is-loading');
      });
  }

  static parseFiltersFromURL(url) {
    const params = new URLSearchParams(url.split('?')[1] || '');
    const filters = {};
    params.forEach((value, key) => {
      if (!filters[key]) filters[key] = [];
      filters[key].push(value);
    });
    return filters;
  }

  static updateURL(searchParams) {
    window.history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`
    );
  }
}

customElements.define('facet-filters-form', FacetFiltersForm);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    link.setAttribute('role', 'button');
    link.addEventListener('click', this.closeFilter.bind(this));
    link.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const href = event.currentTarget.getAttribute('href');
    // Only allow relative URLs
    if (!href || !href.startsWith('/') || href.startsWith('//') || href.startsWith('javascript:')) {
      console.warn('Blocked potentially unsafe URL:', href);
      return;
    }
    const facetForm = this.closest('facet-filters-form');
    if (facetForm && typeof facetForm.fetchAndUpdate === 'function') {
      FacetFiltersForm.updateURL(href.split('?')[1] || '');
      facetForm.fetchAndUpdate(href);
    } else {
      window.location.href = href;
    }
  }
}

customElements.define('facet-remove', FacetRemove);
